import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../api/baseQuery';
import AiService from './aiService';

// Using centralized baseQueryWithReauth from api/baseQuery.js
export const aiSurveyApi = createApi({
  reducerPath: 'aiSurveyApi',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    // Generate survey using AI service
    generateAiSurvey: builder.mutation({
      // We use queryFn to handle custom logic outside of the standard fetch request
      queryFn: async (surveyOptions, { getState }) => {
        try {
          // Log what we're about to do
          console.log('Generating AI survey with options:', surveyOptions);
          
          // Validate the required options
          if (!surveyOptions.topic) {
            return {
              error: {
                status: 'VALIDATION_ERROR',
                error: 'Topic is required for survey generation'
              }
            };
          }
          
          // Make sure we have at least one question type
          if (!surveyOptions.questionTypes || surveyOptions.questionTypes.length === 0) {
            surveyOptions.questionTypes = ['multiple_choice', 'single_choice', 'yes_no'];
          }
          
          // Set reasonable defaults
          if (!surveyOptions.questionCount || surveyOptions.questionCount < 1) {
            surveyOptions.questionCount = 5;
          } else if (surveyOptions.questionCount > 15) {
            surveyOptions.questionCount = 15; // Limit max questions
          }
          
          // Call our AI service with the validated options
          const generatedSurvey = await AiService.generateSurvey(surveyOptions);
          
          // Log success
          console.log('Successfully generated survey:', {
            title: generatedSurvey.title,
            questionCount: generatedSurvey.questions.length
          });
          
          return { data: generatedSurvey };
        } catch (error) {
          console.error('Error in generateAiSurvey:', error);
          return { 
            error: { 
              status: 'CUSTOM_ERROR', 
              error: 'Failed to generate survey with AI: ' + (error.message || 'Unknown error'),
              originalError: error.message 
            } 
          };
        }
      }
    }),
    
    // Save the AI-generated survey through the regular API
    createSurveyFromAi: builder.mutation({
      query: (surveyData) => ({
        url: '/survey/create',
        method: 'POST',
        body: surveyData,
      }),
      // Transform the response for easier consumption
      transformResponse: (response) => ({
        survey: response.value,
        isSuccess: response.isSuccess,
        message: response.isSuccess ? 'Survey created successfully' : 'Failed to create survey',
      }),
    }),
  }),
});

export const {
  useGenerateAiSurveyMutation,
  useCreateSurveyFromAiMutation,
} = aiSurveyApi;

export const {
  reducer: aiSurveyReducer,
  middleware: aiSurveyMiddleware,
  reducerPath: aiSurveyReducerPath,
} = aiSurveyApi; 